import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import { Filter, FilterContent, FilterActions, FilterCombobox } from '@/components/custom/filter';
import { FilterSelectMulti } from '@/components/custom/filter/filter-select-multi';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import usePermission from '@/hooks/usePermission';
import { JOINHA } from '@/pages/dashboard/home-teacher/teacher-character';
import { ProjectsTabNav } from './ProjectsTabNav';
import { ProjectRowTeacher } from './ProjectRowTeacher';

const welcomeMessage = 'Aqui estão os projetos! Acompanhe as entregas, avalie os grupos e organize as atividades dos seus alunos por aqui. 📋';

// Lista lúdica compartilhada pelas abas Grupos e Individuais — mesmo shape de
// dado (project_group com `projects: []`) e mesmo card hoje (GroupProjectCard.jsx),
// só a apresentação muda. Busca/filtro/exclusão continuam vivendo no
// index.jsx de cada aba (ScreenContentGroupProjects/ScreenContentIndividualProjects),
// que só passa os dados já prontos pra cá.
export function ProjectsPlayfulTeacherList({ uri, selectedTab, courses, classrooms, filters, projects, loading, onDeleteProject }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canCreate = usePermission('Criar projetos', 'Projetos');

  // Mesmo componente do combobox de turma do Feed (src/pages/feed/Default/index.jsx)
  // — permite quebra de linha em nomes longos, diferente do <select> nativo usado
  // antes. Curso usa o mesmo componente pra ficar visualmente igual ao de turma.
  const courseOptions = [{ value: 'blank', label: t('filter.blankLabelAll') }, ...(courses?.map(o => ({ value: o.id, label: o.title })) || [])];

  const classroomOptions = [
    { value: 'blank', label: t('filter.blankLabelAll') },
    ...(classrooms?.map(item => ({
      value: item.id,
      label: `${item?.institution?.name} - ${item?.course?.title} - ${item.title}`
    })) || [])
  ];

  const otherFilters = [
    {
      label: t('metrics.approvedProjects'),
      value: filters.values.approvedProjects,
      onChange: () => {
        filters.setFieldValue('projectsInProgress', false);
        filters.setFieldValue('approvedProjects', !filters.values.approvedProjects);
      }
    },
    {
      label: t('metrics.projectsInProgress'),
      value: filters.values.projectsInProgress,
      onChange: () => {
        filters.setFieldValue('approvedProjects', false);
        filters.setFieldValue('projectsInProgress', !filters.values.projectsInProgress);
      }
    }
  ];

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Projetos',
        isActive: true
      }
    ]
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-20"
        image={JOINHA}
        imageAlt="Personagem do professor"
        imageHeight={215}
        imageOffset={200}
        bubbleCorner="tl"
        bubbleMaxWidth={420}
        text={welcomeMessage}
      />

      <ProjectsTabNav selectedTab={selectedTab} />

      <Filter variant="plain">
        <FilterContent>
          <FilterCombobox
            label={t('filter.labelSelectCourse')}
            value={filters.values.course}
            onValueChange={value => filters.setFieldValue('course', value)}
            options={courseOptions}
          />

          <FilterCombobox
            label={t('filter.labelSelectClassroom')}
            value={filters.values.classroom.value}
            onValueChange={value => {
              const selected = classroomOptions.find(option => option.value === value);
              filters.setFieldValue('classroom', { value, label: selected?.label || t('filter.blankLabelAll') });
            }}
            options={classroomOptions}
            disabled={filters.values.course === 'blank'}
            loading={loading}
          />

          <FilterSelectMulti
            data={otherFilters}
            title="Status"
          />
        </FilterContent>

        {canCreate && (
          <FilterActions>
            <PlayfulCardButton
              className="w-auto px-6"
              onClick={() => navigate('/projetos/criar/passo/curso-turma')}
            >
              Novo projeto
            </PlayfulCardButton>
          </FilterActions>
        )}
      </Filter>

      {loading && <Loader />}

      {!loading && projects?.length === 0 && (
        <EmptyState
          type="project"
          title={t('emptyState.textData')}
          text={t('emptyState.createNewProjectInteract')}
        />
      )}

      {!loading && projects?.length > 0 && (
        <div className="relative flex flex-col gap-3">
          {projects.map((project, index) => (
            <ProjectRowTeacher
              key={project.id}
              data={project}
              index={index}
              onDelete={() => onDeleteProject(project.id)}
            />
          ))}
        </div>
      )}
    </DashboardContent>
  );
}
